import { useEffect, useRef, useState, useCallback } from 'react';

interface PerformanceMetrics {
  fps: number;
  frameTime: number;
  droppedFrames: number;
  cpuTime: number;
}

interface UsePerformanceMonitorOptions {
  targetFPS?: number;
  enabled?: boolean;
  onPerformanceIssue?: (metrics: PerformanceMetrics) => void;
}

export const usePerformanceMonitor = ({
  targetFPS = 120,
  enabled = true,
  onPerformanceIssue
}: UsePerformanceMonitorOptions = {}) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    fps: 0,
    frameTime: 0,
    droppedFrames: 0,
    cpuTime: 0,
  });
  
  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(0);
  const droppedFramesRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);
  
  const targetFrameTime = 1000 / targetFPS;
  
  const measureFrame = useCallback((currentTime: DOMHighResTimeStamp) => {
    if (!enabled) return;
    
    if (lastTimeRef.current !== 0) {
      const deltaTime = currentTime - lastTimeRef.current;
      frameCountRef.current++;
      
      // Check for dropped frames
      if (deltaTime > targetFrameTime * 1.5) {
        droppedFramesRef.current++;
      }
      
      // Calculate metrics every second
      if (frameCountRef.current >= targetFPS) {
        const fps = Math.round((frameCountRef.current * 1000) / (currentTime - lastTimeRef.current + frameCountRef.current * 16.67));
        const avgFrameTime = (currentTime - lastTimeRef.current) / frameCountRef.current;
        
        const newMetrics: PerformanceMetrics = {
          fps: Math.min(fps, targetFPS),
          frameTime: avgFrameTime,
          droppedFrames: droppedFramesRef.current,
          cpuTime: avgFrameTime - 16.67, // Approximate CPU time
        };
        
        setMetrics(newMetrics);
        
        // Trigger callback if performance is below threshold
        if (fps < targetFPS * 0.9 && onPerformanceIssue) {
          onPerformanceIssue(newMetrics);
        }
        
        // Reset counters
        frameCountRef.current = 0;
        droppedFramesRef.current = 0;
      }
    }
    
    lastTimeRef.current = currentTime;
    rafIdRef.current = requestAnimationFrame(measureFrame);
  }, [enabled, targetFPS, targetFrameTime, onPerformanceIssue]);
  
  useEffect(() => {
    if (!enabled) return;
    
    // Start monitoring
    rafIdRef.current = requestAnimationFrame(measureFrame);
    
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [enabled, measureFrame]);
  
  const reset = useCallback(() => {
    frameCountRef.current = 0;
    lastTimeRef.current = 0;
    droppedFramesRef.current = 0;
    setMetrics({
      fps: 0,
      frameTime: 0,
      droppedFrames: 0,
      cpuTime: 0,
    });
  }, []);
  
  return {
    metrics,
    reset,
    isPerformant: metrics.fps >= targetFPS * 0.9,
  };
};